import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export type ChatbotModel = "qwen2.5:1.5b" | "llama";

export interface AskAiRequest {
  question: string;
  model: ChatbotModel;
  video_id?: string; // Optional, only needed for video-related queries
}

export interface AskAiResponse {
  question: string;
  answer: string;
}

export interface DocumentItem {
  docId: string;
  status: string;
  stage: string | null;
  chunkCount: number | null;
  tookMs: number | null;
  error: string | null;
  updatedAt: string | null;
}

export interface DocumentsListResponse {
  count: number;
  items: DocumentItem[];
  offset: number;
  limit: number;
}

export interface InterviewRequest {
  docIds: string[];
  query: string;
  interviewMode?: boolean;
  elapsedMinutes?: number;
  systemPrompt?: string;
}
export interface LlmGenerateRequest {
  prompt: string;
  system: string;
  user: string;
}

export interface LlmGenerateResponse {
  text: string;
}

export interface InterviewTurnData {
  q: number;
  current_tier: "easy" | "medium" | "hard";
  next_tier: "easy" | "medium" | "hard";
  score: number;
  avg_score: number;
  feedback: string;
  next_question: string;
  close: boolean;
  verdict: string;
}

export interface InterviewResponse {
  docId: string;
  docIds: string[];
  query: string;
  answer: any[];
  raw_llm?: string;
}

const AI_BASE_URL = import.meta.env.VITE_TEST_FRESHERS_AI_CHATBOT_BASE_URL;
// const AI_BASE_URL = "http://localhost:8000";

export const AiApiSlice = createApi({
  reducerPath: "aiApi",
  baseQuery: fetchBaseQuery({
    baseUrl: AI_BASE_URL,
    prepareHeaders: (headers) => {
      headers.set("x-api-key", "VLJwNdORFB2D5xpbnTrWPjacR0h5Fnm1lKSz_cG-6O4=");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    askAi: builder.mutation<AskAiResponse, AskAiRequest>({
      query: (payload) => ({
        url: "/ask",
        method: "POST",
        body: payload,
      }),
    }),
    askVideoAi: builder.mutation<AskAiResponse, AskAiRequest>({
      query: (payload) => ({
        url: "/ask-video",
        method: "POST",
        body: payload,
      }),
    }),
    uploadVideo: builder.mutation<
      { docId: string; status: string; stage: string },
      FormData
    >({
      query: (formData) => ({
        url: "/documents/upload",
        method: "POST",
        body: formData,
      }),
    }),
    getVideosList: builder.query<{ videos: string[] }, void>({
      query: () => ({
        url: "/videos",
        method: "GET",
      }),
    }),
    getDocumentStatus: builder.query<
      { status: string; stage: string; docId: string },
      string
    >({
      query: (docId) => ({
        url: `/documents/status/${docId}`,
        method: "GET",
      }),
    }),
    getDocumentsList: builder.query<DocumentsListResponse, string | void>({
      query: () => ({
        url: "/documents/list?include_status=true",
        method: "GET",
      }),
    }),
    queryInterview: builder.mutation<InterviewResponse, InterviewRequest>({
      query: (payload) => ({
        url: "/documents/query",
        method: "POST",
        body: payload,
      }),
    }),
    generateLlm: builder.mutation<LlmGenerateResponse, LlmGenerateRequest>({
      query: (payload) => ({
        url: "/llm/generate",
        method: "POST",
        body: payload,
      }),
    }),
    speechToText: builder.mutation<{ text: string }, FormData>({
      query: (formData) => ({
        url: "/documents/speechToText",
        method: "POST",
        body: formData,
      }),
    }),
    textToSpeech: builder.mutation<Blob, { text: string; voice?: string }>({
      query: (payload) => ({
        url: "/documents/textToSpeech",
        method: "POST",
        body: payload,
        responseHandler: (response) => response.blob(),
      }),
    }),
    generateSmartQuery: builder.mutation<{ response: string }, { prompt: string }>({
      query: (payload) => ({
        url: `/api/generate`,
        method: "POST",
        body: {
          prompt: payload.prompt,
        },
      }),
    }),
  }),
});

export const {
  useAskAiMutation,
  useAskVideoAiMutation,
  useUploadVideoMutation,
  useGetVideosListQuery,
  useGetDocumentsListQuery,
  useLazyGetDocumentsListQuery,
  useQueryInterviewMutation,
  useGenerateLlmMutation,
  useLazyGetDocumentStatusQuery,
  useSpeechToTextMutation,
  useTextToSpeechMutation,
  useLazyGetVideosListQuery,
  useGenerateSmartQueryMutation,
} = AiApiSlice;
